import { appContext } from '../../AppContext';
import { fileUpload } from '../../utils/dragndrop/fileUpload';
import { processFiles } from '../../utils/dragndrop/processFiles';
import { restrictFiles } from '../../utils/dragndrop/restrictFiles';
import { sortFiles } from '../../utils/dragndrop/sortFiles';
import DragNDrop from './DragNDrop/DragNDrop';
import { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BASE_FEE } from '../../utils/config';
import { feeUpdater } from '../../utils/validateprogress/payment';
import { setLoadingWithDelay } from '../../utils/loadingHelpers';
import { Validation } from './Validation/Validation';

import ValidationComplete from './ValidationComplete';
import ErrorModal from './ErrorModal';
import NFTModal from './NFTModal';
import { updateJsonTimestampHistoryRecord } from '../../services/sql/sqlHelpers';

const Validate = () => {
  // State to manage validation messages, such as exceeded file size or file batch
  const [validationMsg, setValidationMsg] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [draggingNotAllowed, setDraggingNotAllowed] = useState(false);
  const [addingFiles, setAddingFiles] = useState(false); // Tracks if files are being added
  const [disableAnimation, setDisableAnimation] = useState(false);
  const [fee, setFee] = useState(BASE_FEE);
  const [mintOption, setMintOption] = useState(false);
  const [fileErrors, setFileErrors] = useState<SortError[]>([]);
  const [rejectedFilesArray, setRejectedFilesArray] = useState<IRejectedFiles[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [pendingMsg, setPendingMsg] = useState('');
  const [usrBalance, setUsrBalance] = useState<IBalanceResponse>();
  const [pendingIsOpen, setPendingIsOpen] = useState(false);
  const [verifyBtnText, setVerifyBtnText] = useState('Verify'); // TODO delete if not used
  const [processFileStatus, setProcessFileStatus] = useState('pending');
  const [delayDragging, setDelayDragging] = useState(false);
  const {
    files,
    setFiles,
    validateState,
    validationComplete,
    setValidationComplete,
    uploadLimitReached,
    setUploadLimitReached,
    JSONfileCombined,
    setJSONfileCombined,
  } = useContext(appContext);

  const location = useLocation();

  useEffect(() => {
    if (!isDragging) {
      // On drop, hide content, and then show after a short delay
      setDelayDragging(true);
      const timeout = setTimeout(() => {
        setDelayDragging(false); // Set to true after delay
      }, 400); // Delay in ms

      return () => clearTimeout(timeout); // Clean up timeout on unmount
    }
  }, [isDragging]);

  useEffect(() => {
    feeUpdater(setFee, files, mintOption);
  }, [files]);

  // Clear all if user leaves page
  useEffect(() => {
    if (validationComplete) {
      clearAll();
    }
  }, [location]);

  // Clear all if files.length is 0
  useEffect(() => {
    if (files.length === 0) {
      setJSONfileCombined(false);
    }

    if (
      files.length === 10 ||
      JSONfileCombined ||
      (files[0]?.jsonContent && files[0]?.validationMethod !== 'timestamp-proof')
    ) {
      setUploadLimitReached(true);
    } else if (!JSONfileCombined) {
      setUploadLimitReached(false);
    }
  }, [files, JSONfileCombined]);

  useEffect(() => {
    if (rejectedFilesArray.length > 0 || fileErrors.length > 0) {
      // console.log('fileErrors', fileErrors);
      setShowErrorModal(true);
    }
  }, [rejectedFilesArray, fileErrors, validationMsg]);

  // useEffect(() => {
  //   // Only proceed if we have exactly 2 files and one of them is JSON
  //   if (
  //     files.length === 2 &&
  //     (files[0]?.file?.type === 'application/json' || files[1]?.file?.type === 'application/json')
  //   ) {
  //     // Find the indices of the JSON file and non-JSON file
  //     const jsonFileIndex = files.findIndex((file) => file.file.type === 'application/json');
  //     const sourceFileIndex = jsonFileIndex === 0 ? 1 : 0;

  //     setFiles((prevFiles) => {
  //       // Create a copy of the current files
  //       const updatedFiles = [...prevFiles];

  //       // Update the source file with JSON content from the JSON file
  //       updatedFiles[sourceFileIndex] = {
  //         ...updatedFiles[sourceFileIndex],
  //         jsonFileName: updatedFiles[jsonFileIndex].file.name,
  //         jsonContent: updatedFiles[jsonFileIndex].jsonContent,
  //         validationMethod: 'timestamp',
  //       };

  //       // Extract values from the updated source file
  //       const { jsonContent, validationMethod, fileHash } = updatedFiles[sourceFileIndex];
  //       const stringJsonContent = JSON.stringify(jsonContent);

  //       // Update history record
  //       updateJsonTimestampHistoryRecord(
  //         updatedFiles[jsonFileIndex].file.name,
  //         stringJsonContent,
  //         validationMethod ?? '',
  //         fileHash,
  //       );

  //       // Remove the JSON file and keep only the updated source file
  //       return [updatedFiles[sourceFileIndex]];
  //     });

  //     setJSONfileCombined(true);
  //   }
  // }, [processFileStatus]);

  // Clears all uploaded files if validation not in progress
  const clearAll = () => {
    if (!validateState) {
      setFiles([]);
      setValidationComplete(false);
      setAddingFiles(false);
      setUploadLimitReached(false);
    }
  };

  const handleUploadMoreFiles = () => {
    setDisableAnimation(true);
    clearAll();
  };

  const handleFileUpload = async (event) => {
    const loadingDelay = setLoadingWithDelay(setAddingFiles);

    const selectedfiles = await fileUpload(
      event,
      setValidationMsg,
      validationComplete,
      validateState,
      clearAll,
      setIsDragging,
    );
    setProcessFileStatus('pending');

    const { sortedFiles, rejectedFiles, sortErrors } = await sortFiles(selectedfiles, files);

    setRejectedFilesArray(rejectedFiles);

    setFileErrors(sortErrors);

    // Pass the selected files to the restrictFiles function to be processed
    if (sortedFiles) {
      // const { validFiles, rejectedFiles, errors } = await restrictFiles(
      //   sortedFiles,
      //   setAddingFiles,
      //   files,
      // );

      // setRejectedFilesArray(rejectedFiles);
      // if (sortErrors.length === 0) {
      //   setFileErrors(errors);
      // }

      // console.log('sortedFiles', sortedFiles);
      const sortedFilesArr: CustomFile[] = [];
      const processedFile: CustomFile = {
        file: sortedFiles[0] as unknown as File,
        fileData: new ArrayBuffer(0),
        fileHash: '',
        progress: 'Ready for submit',
        validated: false,
        label: (sortedFiles[0] as unknown as File).name,
        validationMethod: 'timestamp',
      };

      sortedFilesArr.push(processedFile);

      setFiles(sortedFilesArr);
      setProcessFileStatus('complete');
      loadingDelay();
      // if (validFiles) {
      //   processFiles(
      //     validFiles,
      //     files,
      //     setFiles,
      //     validationComplete,
      //     setValidationComplete,
      //     loadingDelay,
      //     setProcessFileStatus,
      //   );
      // } else {
      //   setValidationMsg(`No valid files.`);
      // }
    } else {
      setValidationMsg(`No valid files.`);
    }

    // const jsonFileUploaded = [...selectedFiles].some((file) => file.type === 'application/json');
  };
  // console.log('validateState', validateState);
  // console.log('files', files);
  // console.log('addingFiles', addingFiles);
  // console.log(
  //   'isDragging ',
  //   isDragging,
  //   'delayDragging',
  //   delayDragging,
  //   'showErrorModal',
  //   showErrorModal,
  //   'validateState',
  //   validateState,
  //   'pendingIsOpen',
  //   pendingIsOpen,
  // );
  // Handles drag events entering the drop area
  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!validateState || uploadLimitReached || draggingNotAllowed) {
      event.preventDefault();
      setIsDragging(true);
    } else {
      setDraggingNotAllowed(true); // Prevent dragging if validation is in progress
    }
  };

  // Handles drag events leaving the drop area
  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!validateState || uploadLimitReached) {
      event.preventDefault();
      // Check if the drag leave event is outside the drop area
      if (!event.currentTarget.contains(event.relatedTarget as Node)) {
        setIsDragging(false);
      }
    } else {
      if (!event.currentTarget.contains(event.relatedTarget as Node)) {
        setDraggingNotAllowed(false);
      }
    }
  };

  // Prevent default drag-over behavior and show custom cursor
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault(); // Prevent the default drag-and-drop UI (file icon and mouse pointer)
    if (
      validateState ||
      uploadLimitReached ||
      rejectedFilesArray.length > 0 ||
      fileErrors.length > 0
    ) {
      event.dataTransfer.dropEffect = 'none'; // Prevent drop if validation is in progress
    } else {
      event.dataTransfer.dropEffect = 'copy'; // Allow file dropping
    }
  };

  return (
    <>
      {!validationComplete ? (
        <section
          onDrop={handleFileUpload}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          className={`flex flex-col items-center justify-between w-full max-w-[424px] min-w-[280px] h-[540px] p-4 sm:p-6 md:p-10 bg-black text-white rounded-3xl 
            transition-all duration-300
          ${isDragging && !showErrorModal && !validateState && 'bg-core-grey-20'}
          ${
            !disableAnimation && !validateState
              ? 'animate-slide-up-main'
              : !showErrorModal && 'animate-slide-up-main'
          }
          `}

          // ${uploadLimitReached ? 'opacity-50' : ''}
        >
          {pendingIsOpen && (
            <NFTModal
              message={pendingMsg}
              fee={fee}
              balance={usrBalance}
              pendingIsOpen={pendingIsOpen}
              setPendingIsOpen={setPendingIsOpen}
            />
          )}
          {showErrorModal && (
            <ErrorModal
              fileErrors={fileErrors}
              setFileErrors={setFileErrors}
              rejectedFilesArray={rejectedFilesArray}
              setRejectedFilesArray={setRejectedFilesArray}
              setShowErrorModal={setShowErrorModal}
            />
          )}

          <>
            <DragNDrop
              delayDragging={delayDragging}
              handleFileUpload={handleFileUpload}
              addingFiles={addingFiles}
              pendingIsOpen={pendingIsOpen}
              showErrorModal={showErrorModal}
              isDragging={isDragging}
            />
            {(!validateState && isDragging) || delayDragging ? null : (
              <div className="flex flex-col w-full gap-2">
                <Validation
                  fee={fee}
                  setFee={setFee}
                  setValidationMsg={setValidationMsg}
                  mintOption={mintOption}
                  setMintOption={setMintOption}
                  addingFiles={addingFiles}
                  pendingMsg={pendingMsg}
                  setPendingMsg={setPendingMsg}
                  usrBalance={usrBalance}
                  setUsrBalance={setUsrBalance}
                  pendingIsOpen={pendingIsOpen}
                  setPendingIsOpen={setPendingIsOpen}
                  showErrorModal={showErrorModal}
                  verifyBtnText={verifyBtnText}
                  fileErrors={fileErrors}
                  setFileErrors={setFileErrors}
                />
              </div>
            )}
          </>
        </section>
      ) : (
        <ValidationComplete handleUploadMoreFiles={handleUploadMoreFiles} type="verification" />
      )}
    </>
  );
};

export default Validate;
